/**
 * Reusable inline ✓ Ack for an urgent anomaly DETAIL PANEL (#391).
 *
 * The Anomaly Queue rows have had a ✓ Ack since #140, but the detail-panel bodies
 * the operator opens from the MAP did not — only flood + ice-storm carried one (via
 * their legacy own-status path). So clicking an urgent CAP / hotspot / smoke /
 * media-signal dot gave the operator no way to acknowledge without going back to
 * the queue (#391 / sim-E finding "CAP Extreme has no Ack button").
 *
 * This control closes that gap by REUSING the exact unified-workstate ack path the
 * queue's ✓ Ack already uses — `PUT /api/anomaly-workstate/{source}/{id}` with the
 * same first-acknowledge-wins seat claim, the same `acknowledge_anomaly` audit verb,
 * and the same cross-window broadcast. Workstate is the single source of truth, so
 * the map dot (URGENT-RED → ACK-STEEL on its next read) AND the queue row stay in
 * sync automatically — no parallel ack path.
 *
 * Visibility mirrors the queue ROW (see anomalyQueue.isPanelAckEligible): it shows
 * only for queue-eligible items — a SCORED hotspot/cap in the urgent band (RL≥3) or
 * an UNSCORED smoke/media "needs assessment" item — rendering the ✓ Ack while
 * unacked and a static "Acknowledged" chip once acked.
 *
 * NOT wired into FloodAnomalyBody / IceStormAnomalyBody — those keep their legacy
 * /api/{family}-anomalies/:id/acknowledge path; a future workstate unification is
 * out of scope (#383/#391 Phase 1).
 */
import { useEffect, useState } from 'react';
import { putAnomalyWorkstate } from './api';
import { isPanelAckEligible, type QueueItem } from './anomalyQueue';
import { getSessionId, logAction } from './lib/operatorActions';
import { useSeatName } from './lib/seatIdentity';
import { broadcastWorkstate, useWorkstateBridge } from './workstateBridge';
import { ACK_STEEL } from './layers/anomalyMarker';

export function AnomalyAckButton({
  item,
  onAcked,
}: {
  /** The queue item built from the panel's feature (e.g. hotspotToQueueItem(f)). */
  item: QueueItem;
  /** Optional hook fired after a successful ack (e.g. nudge a queue refresh). */
  onAcked?: () => void;
}): JSX.Element | null {
  const [seat] = useSeatName();
  const [acked, setAcked] = useState(item.acknowledged);
  const [busy, setBusy] = useState(false);

  // Resync to server truth when the selected item changes or its persisted ack
  // flips on a refetch. The optimistic setAcked(true) below survives because the
  // prop's `acknowledged` is unchanged until the parent refetch lands.
  useEffect(() => {
    setAcked(item.acknowledged);
  }, [item.source, item.sourceId, item.acknowledged]);

  // Cross-window (#264): a sibling /panel acking THIS exact item reflects here too.
  useWorkstateBridge((msg) => {
    if (msg.sourceType === item.source && msg.sourceId === item.sourceId) {
      setAcked(msg.work_acknowledged_at != null);
    }
  });

  // Eligibility decides whether this control exists at all (after the hooks, per the
  // rules of hooks). Ack STATE then picks button-vs-chip.
  if (!isPanelAckEligible(item)) return null;

  const ack = async (): Promise<void> => {
    if (busy || acked) return;
    setBusy(true);
    setAcked(true); // optimistic — the queue ✓ Ack patches its row the same way
    const claiming = seat != null;
    try {
      const res = await putAnomalyWorkstate(
        item.source,
        item.sourceId,
        claiming ? { acknowledged: true, owner_label: seat, claim: true } : { acknowledged: true },
        getSessionId(),
      );
      logAction('acknowledge_anomaly', {
        item_id: item.id,
        source: item.source,
        from: 'detail_panel',
        ...(claiming ? { claim: true, claimed: res.claimed !== false, seat } : {}),
      });
      // Server truth — on a lost claim this stays acknowledged (someone else owns it).
      setAcked(res.work_acknowledged_at != null);
      broadcastWorkstate(res);
      onAcked?.();
    } catch (err) {
      setAcked(false); // revert so the operator can retry
      logAction('acknowledge_anomaly', {
        item_id: item.id,
        source: item.source,
        from: 'detail_panel',
        ...(claiming ? { claim: true, seat } : {}),
        persist_failed: true,
      });
      console.error('panel ack persist failed', err);
    } finally {
      setBusy(false);
    }
  };

  if (acked) {
    return (
      <span
        data-testid="anomaly-ack-done"
        className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium"
        style={{ backgroundColor: `${ACK_STEEL}22`, color: ACK_STEEL }}
        title="Acknowledged — demoted in the queue; the marker stays (audit-safe)."
      >
        ✓ Acknowledged
      </span>
    );
  }

  return (
    <button
      type="button"
      data-testid="anomaly-ack-button"
      disabled={busy}
      onClick={() => void ack()}
      className="inline-flex items-center gap-1 rounded bg-emerald-600/80 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
      title={
        seat
          ? `Acknowledge & claim for ${seat} (first ack claims) — demotes; marker stays`
          : 'Acknowledge — demotes in the queue; the marker stays'
      }
    >
      {busy ? 'Acknowledging…' : '✓ Acknowledge'}
    </button>
  );
}
